import classNames from "classnames";
import { NavLink } from "react-router-dom";

import { useLoggedInAuth } from "auth/AuthContext";
import { Icon } from "components/Icon/Icon";
import { Popover } from "components/Popover/Popover";
import { useLogout } from "contexts/User/useLogout";
import { useUser } from "contexts/User/UserContext";
import { useTargetState } from "hooks";
import { useIsDesktop } from "hooks/useMediaQuery";
import { useTranslation } from "i18n";
import { routes } from "routes";
import { AccountMenuItem, resolveSidebarLabel } from "types";

export const AccountMenu = ({ items }: { items: AccountMenuItem[] }) => {
  const t = useTranslation();
  const isDesktop = useIsDesktop();

  const accessProps = useUser();
  const { hasPermission } = accessProps;

  const [target, setTarget] = useTargetState();
  const [logout] = useLogout();
  const { currentImpersonationUuid } = useLoggedInAuth();

  return (
    <>
      <button
        id="account-menu-toggle" // Using this for E2E tests.
        onClick={setTarget}
        className={classNames("w-52 lg:w-80 h-56 flex-center", {
          "bg-grey-200": currentImpersonationUuid,
        })}
      >
        {currentImpersonationUuid ?? <Icon name="incognito" />}
      </button>
      {target && (
        <Popover
          target={target}
          position={isDesktop ? "top-left" : "bottom-right"}
          className={classNames(
            "flex-col divide-y",
            isDesktop ? "mt-4 ml-52" : "mt-4 mr-10",
          )}
          allowScrolling
          noArrow
          onClose={() => setTarget(undefined)}
        >
          <div className="py-8">
            {items.map(({ to, icon, label }) => (
              <NavLink
                key={to}
                onClick={() => setTarget(undefined)}
                to={to}
                className={classNames(
                  "h-40 p-8 pr-10 flex items-center text-14 list-element border-none",
                )}
                activeClassName="text-primary bg-primary-100 font-medium"
              >
                <Icon name={icon} size={20} className="mx-8" />
                <div className="mr-8">
                  {resolveSidebarLabel(label, accessProps)}
                </div>
              </NavLink>
            ))}
          </div>
          {hasPermission("SEND_HELP_MESSAGES") && (
            <div className="py-8">
              <NavLink
                key={routes.HELP}
                onClick={() => setTarget(undefined)}
                to={routes.HELP}
                className={classNames(
                  "h-40 p-8 pr-10 flex items-center text-14 list-element border-none",
                )}
                activeClassName="text-primary bg-primary-100 font-medium"
              >
                <Icon name="email" size={20} className="mx-8" />
                <div className="mr-8">
                  {t("main_sidebar.account_menu.help")}
                </div>
              </NavLink>
            </div>
          )}
          {!currentImpersonationUuid && (
            <div className="py-8">
              <button
                onClick={logout}
                className={classNames(
                  "h-40 p-8 pr-10 flex items-center text-14 list-element border-none",
                )}
              >
                <Icon name="logout" size={20} className="mx-8" />
                <div className="mr-8">
                  {t("main_sidebar.account_menu.log_out")}
                </div>
              </button>
            </div>
          )}
        </Popover>
      )}
    </>
  );
};
